import { z } from "zod";
/**
 * The API shape of a user — what crosses the wire, not what sits in Postgres.
 *
 * `packages/db` owns the `users` *table* and is free to carry columns that
 * never appear here (billing state, soft-delete). Block 00b must not re-derive
 * this type from Drizzle: `@nook/schema` is the single source of truth for
 * request/response shapes and both sides import it. (plan §2)
 *
 * This is the `GET /v1/me` body. Block 01 revises the field list the 00b
 * placeholder guessed at — `name`/`avatarUrl` had no columns behind them, and
 * `plan`/`trialEndsAt` are what every caller actually needs. camelCase, like
 * every other schema in this package; the block spec's snake_case
 * `trial_ends_at` would have made this the only snake_case body in the API.
 */
export const userSchema = z.object({
    id: z.uuid(),
    email: z.email(),
    plan: z.enum(["trialing", "pro", "expired"]),
    trialEndsAt: z.iso.datetime().nullable(),
    /** Active linked UAT grant expiry; null when inactive, absent when lookup is unknown. */
    uatExpiresAt: z.iso.datetime().nullable().optional(),
    /** Next billing date while `pro`, from Dodo (block 31, A8 C7). */
    planRenewsAt: z.iso.datetime().nullable(),
    /** `pro`, but cancelled: access runs to `planRenewsAt`, then stops (block 32). */
    cancelAtPeriodEnd: z.boolean(),
    /** A Dodo customer exists, so the billing portal can open (block 32). */
    hasBillingAccount: z.boolean(),
    /**
     * Member since, for the Account card (block 33, A9 C1). Optional until the
     * backend that sends it is live: Pages ships the webapp on push to `main`,
     * ahead of the approval-gated backend, and a required field would fail
     * every `/v1/me` parse in that gap. Make it required in a later change.
     */
    createdAt: z.iso.datetime().optional(),
    /**
     * The Google account picture, from the verified Supabase identity rather than
     * a column. Optional for the same Pages-ahead-of-backend reason as `createdAt`,
     * and absent where a user is resolved without a JWT (MCP, jobs).
     */
    avatarUrl: z.string().nullish(),
});
/** `POST /v1/account/export` → `202`. The ZIP arrives by email (block 33). */
export const exportJobSchema = z.object({ jobId: z.uuid() });
/** `DELETE /v1/account` body, optional. iOS sends a fresh Sign in with Apple code so the backend can revoke Apple's tokens. */
export const deleteAccountSchema = z.object({ appleAuthorizationCode: z.string().min(1).optional() });
/** `DELETE /v1/account` → `200`. Soft-deleted now, purged after 60 days (block 34). */
export const accountDeletedSchema = z.object({ deletedAt: z.iso.datetime() });
//# sourceMappingURL=user.js.map